import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HttpErrorResponse } from '@endurancewarranty/lib';
import { CustomValidationError } from '@lib/errors/CustomValidationError';
import { useIsPopupWindow } from '@lib/hooks/useIsPopupWindow';
import { errorAtom } from '@lib/states/error/errorAtom';
import { useSetJotaiAtom } from '@lib/states/jotai/hooks';
import { customErrorAtom } from '@states/customError/customErrorAtom';
import type { QueryOptions } from '@tanstack/react-query';
import type { FC, ReactNode } from 'react';
const HTTP_STATUS_NO_RETRY = [400, 401, 403, 404];
// 3 is the default React Query retry count
const MAX_RETRY_ATTEMPTS = 3;
type Props = Readonly<{
  children: ReactNode;
}>;
function isCustomValidationError(error: unknown): error is CustomValidationError {
  return error instanceof CustomValidationError;
}
export const QueryClientContext: FC<Props> = t0 => {
  const $ = _c(21);
  const {
    children
  } = t0;
  const isPopup = useIsPopupWindow();
  const setErrors = useSetJotaiAtom(errorAtom);
  const setCustomErrors = useSetJotaiAtom(customErrorAtom);
  let t1;
  if ($[0] !== setErrors) {
    t1 = error => {
      if (HttpErrorResponse.isHttpErrorResponse(error)) {
        const statusCode = error.getHttpErrorMetadata().statusCode;
        if (statusCode === 400) {
          setErrors(prevState => [...prevState, {
            error,
            context: "bad_request"
          }]);
          return;
        }
        if (statusCode === 401) {
          setErrors(prevState_0 => [...prevState_0, {
            error,
            context: "unauthenticated"
          }]);
          return;
        }
        if (statusCode === 403) {
          setErrors(prevState_1 => [...prevState_1, {
            error,
            context: "unauthorized"
          }]);
          return;
        }
        if (statusCode >= 500) {
          setErrors(prevState_2 => [...prevState_2, {
            error,
            context: "default"
          }]);
          return;
        }
      }
    };
    $[0] = setErrors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setErrorContext = t1;
  let t2;
  if ($[2] !== setCustomErrors) {
    t2 = error_0 => {
      setCustomErrors(prevState_3 => ({
        ...prevState_3,
        error: error_0
      }));
    };
    $[2] = setCustomErrors;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const setCustomErrorContext = t2;
  let t3;
  if ($[4] !== setCustomErrorContext || $[5] !== setErrorContext) {
    t3 = (error_1, _variables, _context, mutation) => {
      if (mutation.options.meta?.overrideDefaultHttpErrorHandling === true) {
        return;
      }
      if (isCustomValidationError(error_1)) {
        if (mutation.options.meta?.showErrorMessageInline === true) {
          return;
        }
        setCustomErrorContext(error_1);
        return;
      }
      setErrorContext(error_1);
    };
    $[4] = setCustomErrorContext;
    $[5] = setErrorContext;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  let t5;
  if ($[7] !== setCustomErrorContext || $[8] !== setErrorContext || $[9] !== t3) {
    const mutationCache = new MutationCache({
      onError: t3
    });
    let t6;
    if ($[12] !== setCustomErrorContext || $[13] !== setErrorContext) {
      t6 = (error_2, query) => {
        if (query.options.meta?.overrideDefaultHttpErrorHandling === true) {
          return;
        }
        if (isCustomValidationError(error_2)) {
          if (query.options.meta?.showErrorMessageInline === true) {
            return;
          }
          setCustomErrorContext(error_2);
          return;
        }
        setErrorContext(error_2);
      };
      $[12] = setCustomErrorContext;
      $[13] = setErrorContext;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const queryCache = new QueryCache({
      onError: t6
    });
    const retryOptions = {
      retry: _temp
    } satisfies QueryOptions;
    t4 = useState;
    t5 = new QueryClient({
      mutationCache,
      queryCache,
      defaultOptions: {
        queries: {
          ...retryOptions,
          refetchOnWindowFocus: false
        }
      }
    });
    $[7] = setCustomErrorContext;
    $[8] = setErrorContext;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  const [queryClient] = t4(t5);
  let t6;
  if ($[15] !== isPopup) {
    t6 = !isPopup && <ReactQueryDevtools initialIsOpen={false} position="bottom" />;
    $[15] = isPopup;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== children || $[18] !== queryClient || $[19] !== t6) {
    t7 = <QueryClientProvider client={queryClient}>{children}{t6}</QueryClientProvider>;
    $[17] = children;
    $[18] = queryClient;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
function _temp(failureCount, error_3) {
  if (failureCount > MAX_RETRY_ATTEMPTS) {
    return false;
  }
  if (HttpErrorResponse.isHttpErrorResponse(error_3)) {
    const code = error_3.getHttpErrorMetadata().statusCode;
    return !HTTP_STATUS_NO_RETRY.includes(code);
  }
  return false;
}